// src/utils/pdf/Report-Standby-All-OS.ts

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import lecsorLogo from "../../assets/icons/lecsor-logo-B3uTogru.svg";

export type AllOSMaterial = {
  codigo: string;
  nome: string;
  tecnico: string;
  quantidade?: number | null;
  custoUnitario?: number | null;
};

async function svgToPngDataUrl(svgUrl: string): Promise<string> {
  const response = await fetch(svgUrl);
  const svgText = await response.text();

  const blob = new Blob([svgText], {
    type: "image/svg+xml;charset=utf-8",
  });

  const blobUrl = URL.createObjectURL(blob);

  try {
    const img = new Image();

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Erro ao carregar logo SVG"));
      img.src = blobUrl;
    });

    const canvas = document.createElement("canvas");

    const scale = 4;

    canvas.width = (img.naturalWidth || 300) * scale;
    canvas.height = (img.naturalHeight || 100) * scale;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Não foi possível criar o canvas da logo");
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export async function generatePdfReportAllOS({
  osNumero,
  tecnicos, // lista única de nomes
  materiais, // todas as movimentações da OS
}: {
  osNumero: string;
  tecnicos: string[];
  materiais: AllOSMaterial[];
}) {
  const logoImg = await svgToPngDataUrl(lecsorLogo);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageW = doc.internal.pageSize.getWidth(); // ~297
  const pageH = doc.internal.pageSize.getHeight(); // ~210
  const gutter = 6;
  const panelW = (pageW - gutter) / 2;
  const leftX = 0;
  const rightX = panelW + gutter;

  function drawCompanyHeader(panelX: number) {
    const marginLeft = panelX + 10;
    const rightX = panelX + (panelW - 10);

    try {
      doc.addImage(logoImg, "PNG", marginLeft + 0, 10, 27, 13);
    } catch {}

    const companyInfo = [
      "**LECSOR TECHNOLOGY LTDA**",
      "**https://lecsor.com.br/**",
      "",
      "CNPJ: 28.366.167/0001-90",
      "",
      "R DELEGADO WALDIR GUILHERME, 32",
      "ILHA DA CONCEICAO",
      "Niteroi - RJ - CEP: 24050-170",
      "Telefone: (21) 3614-0926",
    ];

    doc.setFontSize(9);
    let y = 10;
    let enderecoY = 0;

    companyInfo.forEach((line) => {
      const isBold = line.startsWith("**") && line.endsWith("**");
      const cleanLine = line.replace(/\*\*/g, "");

      doc.setFont("helvetica", isBold ? "bold" : "normal");
      doc.text(cleanLine, rightX, y, { align: "right" });

      if (cleanLine.startsWith("R DELEGADO")) enderecoY = y;

      y += 4;
    });

    const now = new Date();

    const formattedDate = now.toLocaleDateString("pt-BR").replace(/\//g, "/");

    const formattedTime = now.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    doc.text(
      `Data de Geração do Relatório: ${formattedDate}`,
      marginLeft,
      enderecoY + 10
    );

    doc.text(
      `Horário de Geração do Relatório: ${formattedTime}`,
      marginLeft,
      enderecoY + 15
    );

    return { baseY: enderecoY + 20 };
  }

  function drawPanel(panelX: number) {
    const { baseY } = drawCompanyHeader(panelX);

    const marginX = panelX + 10;

    let y = baseY + 10;

    // Campos (sem título no topo)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    doc.text(`O.S.: ${osNumero}`, marginX, y);

    y += 5;

    const tecnicosStr = tecnicos.length ? tecnicos.join(", ") : "-";

    doc.text(`Nomes dos Técnicos: ${tecnicosStr}`, marginX, y);

    // Materiais (tabela sem custos)
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);

    doc.text("Materiais", marginX, y);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const headers = [["Código", "Nome do Produto", "Técnico"]];

    const body = materiais.map((m) => [
      m.codigo || "-",
      m.nome || "-",
      m.tecnico || "-",
    ]);

    autoTable(doc, {
      head: headers,
      body,
      startY: y + 3,

      margin: {
        left: marginX,
        right: marginX,
      },

      tableWidth: panelW - 20,

      styles: {
        fontSize: 9,
        halign: "center",
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.25,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [18, 66, 128],
        textColor: 255,
        fontStyle: "normal",
      },
    });

    // Cálculo do custo total (texto fora da tabela)
    const totalCusto = materiais.reduce((acc, m) => {
      const qtd = Number(m.quantidade ?? 0);
      const custo = Number(m.custoUnitario ?? 0);

      if (Number.isFinite(qtd) && Number.isFinite(custo)) {
        return acc + qtd * custo;
      }

      return acc;
    }, 0);

    const afterTableY = (doc as any).lastAutoTable?.finalY || y + 20;

    const custoY = afterTableY + 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    // Assinaturas
    const sigY = custoY + 10;
    const lineW = panelW - 20;

    doc.setDrawColor(0, 0, 0);

    doc.line(marginX, sigY, marginX + lineW, sigY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    doc.text("Assinatura do Técnico", marginX, sigY + 5);

    const sigY2 = sigY + 18;

    doc.line(marginX, sigY2, marginX + lineW, sigY2);

    doc.text("Assinatura do Responsável pela Liberação", marginX, sigY2 + 5);

    // Rodapé
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");

    doc.text("Relatório gerado pelo sistema Logvi", marginX, sigY2 + 14 + 6);
  }

  drawPanel(leftX);
  drawPanel(rightX);

  const now = new Date();

  const fileStamp = now
    .toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(/[\/:]/g, "-")
    .replace(",", "");

  doc.save(`Relatorio-OS-Completo-${osNumero}-${fileStamp}.pdf`);
}
